import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { Table } from '@/common';
import { Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE } from '@/enums';
import { getOrganizationsColumns, mapOrganizationsToTableData } from '@/helpers';
import { Filters } from '@/pages/Organizations/Filters';
import { useDeleteOrganizationMutation, useGetAllOrganizations } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { AllowedPermissions, Organization, SelectOptionProps, TableData } from '@/types';

export const Organizations = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const { currentUserData: currentUser } = useContext(AuthContext);

  const [appliedFiltersCounter, setAppliedFiltersCounter] = useState<number>(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>();
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>();
  const [filters, setFilters] = useState<{
    status: SingleValue<SelectOptionProps>;
  }>({
    status: null
  });

  const permissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_ORGANIZATION,
    editPermission: PERMISSION_TYPE.UPDATE_ORGANIZATION,
    deletePermission: PERMISSION_TYPE.DELETE_ORGANIZATION
  };
  const hasCreateOrganizationPermission = currentUser?.role?.permissions?.includes(
    PERMISSION_TYPE.CREATE_ORGANIZATION
  );

  const {
    isLoading,
    isSuccess,
    data: allOrganizations,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetAllOrganizations(
    PAGE_SIZE,
    search,
    filters.status?.value as boolean,
    true,
    currentUser?.id
  );

  const { mutate: deleteOrganization } = useDeleteOrganizationMutation(queryClient, {
    onSuccess: () => {
      setSelectedOrganization(undefined);
    },
    onError: () => {
      setSelectedOrganization(undefined);
    }
  });

  const organizations =
    allOrganizations?.pages
      .flatMap((page) => page.data?.items ?? [])
      .filter((organization): organization is Organization => !!organization) ?? [];
  const tableData = mapOrganizationsToTableData(organizations);

  useEffect(() => {
    const numberOfFilters = Object.values(filters).filter((value) => value !== null);
    setAppliedFiltersCounter(numberOfFilters.length);
  }, [filters]);

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const resetFilters = () => {
    setIsFilterModalOpen(false);
    setFilters({ status: null });
    setAppliedFiltersCounter(0);
  };

  const applyFilters = (values: Record<string, SelectOptionProps>) => {
    setIsFilterModalOpen(false);
    setFilters({
      status: values.filterByStatus
    });
  };

  const handleViewDetails = (row: TableData) => {
    navigate(`/organizations/${row.id}`);
  };

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          {isSuccess && organizations.length === 1 && !hasCreateOrganizationPermission ? (
            <Navigate to={`/organizations/${organizations[0].id}`} replace />
          ) : (
            <Table
              data={tableData}
              columns={getOrganizationsColumns()}
              testIdPrefix={TestIds.organizations.prefix}
              tableTitle={t('organizations')}
              addButtonLabel={t('addOrganization')}
              permissions={permissions}
              search={searchCallback}
              handleCreate={() => navigate('/organizations/create')}
              handleEdit={(item) => navigate(`/organizations/${item.id}/edit`)}
              handleDelete={(item) => setSelectedOrganization(item as Organization)}
              handleOpenFilterModal={() => setIsFilterModalOpen(!isFilterModalOpen)}
              handleViewDetails={handleViewDetails}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              numberOfAppliedFilters={appliedFiltersCounter}
              hiddenColumnsMobile={['enabled']}
              mobileColumnOffset={1}
            />
          )}
          {selectedOrganization && (
            <ConfirmationModal
              isOpen={!!selectedOrganization}
              message={t('deleteMessage', { item: selectedOrganization.name })}
              onClose={() => setSelectedOrganization(undefined)}
              onConfirm={() => deleteOrganization(selectedOrganization?.id)}
            />
          )}
          {isFilterModalOpen && (
            <Filters
              apply={applyFilters as (values: Record<string, SelectOptionProps>) => void}
              reset={resetFilters}
              onClose={() => setIsFilterModalOpen(false)}
              filterByStatus={filters.status}
            />
          )}
        </div>
      )}
    </div>
  );
};
